import React, { useState, useRef, useEffect, ReactElement } from 'react';
import styled from '@emotion/styled';
import { css } from 'glamor';
import StatusIcon from '../atoms/icons/StatusIcon';
import { getBorders, getTextColor, getInputTheme } from '../style';
import InputLabel from './InputLabel';
import { isDarkMode } from '../utils/helpers';
import { InputV2 } from 'swui';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { IInputStyle, ITextInputProps } from './types/TextInput';
import { ITooltip } from './types';
import { FONT_FAMILIES } from 'theme/material/constants';

const Input = styled('div')({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  width: '100%',
  '> div:first-of-type': {
    width: '100% !important',
  },
});

const getDefaultBorder = (islogin: string, joinForm: string, status: string | null) => {
  if (islogin === 'true') {
    return '1px solid rgba(0,0,0,0.15)';
  } else if (status === 'error') {
    return '1px solid #DD0B24';
  } else if (status) {
    return `1px solid ${getStatusColor()[status]}`;
  }

  return getBorders().DARKER;
};

const getFocusBorder = (joinForm: string, status: string | null) => {
  if (joinForm === 'true' && status === 'error') {
    return '1px solid #DD0B24';
  } else if (joinForm === 'true') {
    return getBorders().DARKER;
  } else if (status) {
    return `1px solid ${getStatusColor()[status]}`;
  }
  return getBorders().FOCUS;
};

const InputStyle = styled(InputV2)<IInputStyle>(
  () => ({
    color: getInputTheme().colorInput,
    '&:focus': {
      color: getInputTheme().colorInput,
      background: getInputTheme().backgroundInput,
    },
  }),
  ({ islogin, joinForm, status }) => ({
    width: '100%',
    appearance: 'none',
    boxSizing: 'border-box',
    padding: '0 15px',
    height: 40,
    outline: 'none',
    borderRadius: 3,
    background: getInputTheme().backgroundInput,
    color: islogin === 'true' ? '#000000' : getInputTheme().colorInput,
    border: getDefaultBorder(islogin, joinForm, status),
    transition: 'border-color: .2s ease-in-out',
    fontSize: joinForm === 'true' ? 15 : 16,
    fontFamily: FONT_FAMILIES.primary,
    boxShadow: '1px 1px 3px 0px rgba(0, 0, 0, 0.2) inset',
    '&:focus': {
      border: getFocusBorder(joinForm, status),
      color: islogin === 'true' ? '#000000' : getInputTheme().colorInput,
    },
    '&::placeholder': {
      color: `${islogin !== 'true' ? getInputTheme().textInputPlaceholder : '#C9C9C9'} !important`,
    },
  }),
  ({ borderless }) =>
    borderless === 'true' && {
      border: 'none !important',
    }
);

export const getStatusColor = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    success: '#0DB800',
    error: '#DD0B24',
    caution: '#FF7C00',
    notification: '#FF0000',
    default: !themeDarkMode ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.15)',
  };
};

const StatusDescription = styled('div')<{ status: string }>(
  {
    marginTop: 4,
    textAlign: 'center',
    fontSize: 14,
  },
  ({ status }) =>
    status && {
      color: getStatusColor()[status],
      fontWeight: status === 'error' ? 700 : 400,
    }
);

const ShowPassword = styled('div')({
  position: 'absolute',
  right: 0,
  top: 0,
  cursor: 'pointer',
  color: '#c9c9c9',
  fontWeight: 500,
});

const Counter = styled('div')<{ hasError: boolean }>(
  {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '16px',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  ({ hasError }) => ({
    color: !hasError ? '#595959' : getStatusColor().error,
  })
);

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const LabelContainer = styled('div')(() => ({
  position: 'relative',
  minHeight: '17px',
  color: getTextColor().DARK.color,
}));

const InputContainer = styled('div')({
  paddingBottom: 24,
});

const StatusIconContainer = styled('div')({
  position: 'absolute',
  right: 0,
  top: 3,
});

const renderStatusIcon = (status: string, flag: boolean) => {
  if (status === 'caution-error' || status === 'error') {
    return (
      <StatusIconContainer style={{ right: flag ? '20px' : 0, top: flag ? '-3px' : 3 }}>
        <StatusIcon status={`warning`} style={{ color: '#DD0B24' }} />
      </StatusIconContainer>
    );
  }

  return (
    <StatusIconContainer>
      <StatusIcon status={status} />
    </StatusIconContainer>
  );
};

const renderInputLabel = (
  inputLabel: string,
  labelDescription?: string,
  tooltip?: ITooltip,
  inputId?: string,
  isBilling?: boolean,
  customStyleLabel?: React.CSSProperties,
  customContainerStyle?: React.CSSProperties,
  isRequired?: boolean,
  extraLabelInfo?: React.ReactNode
) => {
  return (
    <InputLabel
      label={inputLabel}
      labelDescription={labelDescription}
      tooltip={tooltip}
      inputId={`label-${inputId}`}
      customStyle={customStyleLabel}
      isBilling={isBilling}
      isRequired={isRequired}
      extraLabelInfo={extraLabelInfo}
      customContainerStyle={customContainerStyle}
    />
  );
};

const TextInputV2 = ({
  id,
  onChange,
  type,
  inputLabel,
  tooltip,
  labelDescription,
  eleRef,
  statusMessage,
  status = 'default',
  showPassword = false,
  passwordEyeColor,
  customStyle,
  customInputStyle,
  autoFocus,
  isBilling = false,
  customStyleLabel,
  isLogin = false,
  joinForm = false,
  noBorder = false,
  isRequired = false,
  hideStatusIcon = false,
  showCounter = false,
  maxCount = 0,
  value = '',
  extraLabelInfo,
  customStyleLabelContainer,
  ...rest
}: ITextInputProps): ReactElement => {
  const textInput = useRef(null);
  const [passwordInputType, setPasswordInputType] = useState('password');
  const [hideError, setHideError] = useState(false);
  const darkMode = isDarkMode();

  useEffect(() => {
    if (autoFocus) {
      focusTextInput();
    }
  }, [autoFocus]);

  const focusTextInput = () => {
    if (textInput.current) textInput.current.focus();
  };

  const handleShowPassword = () => {
    setPasswordInputType(passwordInputType === 'text' ? 'password' : 'text');
  };

  const showPasswordToggle = showPassword ? (
    <ShowPassword onClick={handleShowPassword} id="toggle_password">
      {passwordInputType === 'password' ? (
        <Visibility style={{ color: passwordEyeColor ? passwordEyeColor : 'inherit' }} />
      ) : (
        <VisibilityOff style={{ color: passwordEyeColor ? passwordEyeColor : 'inherit' }} />
      )}
    </ShowPassword>
  ) : null;

  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  const hasCounterError = value?.length > maxCount;

  const showValueCounter = (
    <Counter hasError={hasCounterError}>
      {
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        value?.length || 0
      }
      /{maxCount}
    </Counter>
  );

  return (
    <InputContainer ref={eleRef} style={customStyle}>
      {(inputLabel || showPassword) && (
        <LabelContainer style={{ color: isLogin ? 'rgba(0,0,0,.65)' : '' }}>
          {inputLabel &&
            renderInputLabel(
              inputLabel,
              labelDescription,
              tooltip,
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              id,
              isBilling,
              { fontSize: 15, ...customStyleLabel },
              customStyleLabelContainer,
              isRequired,
              extraLabelInfo
            )}
          {showPassword && showPasswordToggle}
          {showCounter && showValueCounter}
          {!hideStatusIcon && status !== 'default'
            ? // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              renderStatusIcon(status, showPassword && showPasswordToggle)
            : null}
        </LabelContainer>
      )}
      <Input className={darkMode && !isLogin ? 'darkmode' : 'lightmode'}>
        <InputStyle
          id={id?.toString()}
          onChange={onChange}
          data-test-id={`text-${id}`}
          type={showPassword ? passwordInputType : type}
          status={status}
          ref={textInput}
          onFocus={() => (isBilling ? setHideError(true) : null)}
          islogin={isLogin.toString()}
          joinForm={joinForm.toString()}
          data-lpignore="true"
          autoComplete="off"
          {...css(customInputStyle)}
          borderless={noBorder ? 'true' : 'false'}
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          value={value}
          {...rest}
        />
        {status && <StatusDescription status={status} />}
      </Input>
      {!hideError && statusMessage && status !== 'default' ? (
        <StatusDescription data-test-id={`${status}-${id}`} status={status}>
          {statusMessage}
        </StatusDescription>
      ) : null}
    </InputContainer>
  );
};

export default TextInputV2;
